import React from "react";
import { Calendar, Gift, Star } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

const Activities: React.FC<{ content?: any }> = ({ content }) => {
  const { t_api } = useLanguage();

  const promo_date = content?.promo_date || "2";
  const promo_year = content?.promo_year || "Febr 2014";
  const promo_title = content?.promo_title || t_api("Dunlop Sales Promotion During April to June 2015", "عرض مبيعات دنلوب خلال الفترة من أبريل إلى يونيو 2015");
  const promo_subtitle = content?.promo_subtitle || t_api("Sales Promotion During April to June 2015 for our Dealers and cash customers all stores of Oman.", "عرض مبيعات خلال الفترة من أبريل إلى يونيو 2015 لوكلائنا والعملاء النقديين في جميع المتاجر في عمان.");
  const promo_image_1 = content?.promo_image_1 || "/Scratch-English_1.jpg";
  const promo_image_2 = content?.promo_image_2 || "/Scratch-Arabic_1.jpg";

  const points = content?.points?.length > 0 ? content.points : [
    { 
      title: t_api("Prize Campaign", "حملة الجوائز"), 
      desc: t_api("Scratch & Win prizes up to 50 OMR", "امسح وربح جوائز تصل إلى 50 ريال عماني"), 
      icon: "Gift" 
    },
    { 
      title: t_api("Seasonal Offers", "عروض موسمية"), 
      desc: t_api("Valid April to June 2015", "صالح من أبريل إلى يونيو 2015"), 
      icon: "Calendar" 
    },
    { 
      title: t_api("Quality Rewards", "مكافآت الجودة"), 
      desc: t_api("Rewarding premium Dunlop tyre purchases", "مكافأة مشتريات إطارات دنلوب الممتازة"), 
      icon: "Star" 
    },
  ];

  const iconMap: Record<string, React.ReactNode> = {
    Gift: <Gift className="h-8 w-8 text-yellow-700 mb-3" />,
    Calendar: <Calendar className="h-8 w-8 text-yellow-700 mb-3" />,
    Star: <Star className="h-8 w-8 text-yellow-700 mb-3" />,
  };

  return (
    <div className="max-w-7xl mx-auto">
      <motion.div
        className="bg-white rounded-xl shadow-lg border overflow-hidden"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {/* Header */}
        <div className="flex items-center bg-gray-50 border-b px-6 py-6">
          <motion.div
            className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-md text-center mr-6"
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="text-xl font-bold">{promo_date}</div>
            <div className="text-xs uppercase tracking-wide">{promo_year}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h2 className="text-1xl md:text-2xl font-extrabold text-gray-900 mb-3">{promo_title}</h2>
            <p className="text-gray-600 text-[15px] md:text-base text-justify mt-1">{promo_subtitle}</p>
          </motion.div>
        </div>

        {/* Promotional Images */}
        <div className="p-8">
          <div className="px-4 sm:px-8 pb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 justify-items-center">
              <motion.div className="flex flex-col items-center w-full" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} viewport={{ once: true }}>
                <div className="bg-white rounded-xl border shadow-lg overflow-hidden p-1 hover:shadow-2xl transition">
                  <img src={promo_image_1} alt="Promotion English" className="w-full max-w-md h-auto object-contain" />
                </div>
              </motion.div>
              <motion.div className="flex flex-col items-center w-full" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} viewport={{ once: true }}>
                <div className="bg-white rounded-xl border shadow-lg overflow-hidden p-1 hover:shadow-2xl transition">
                  <img src={promo_image_2} alt="Promotion Arabic" className="w-full max-w-md h-auto object-contain" />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Feature Points */}
          <motion.div
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.2 } } }}
            viewport={{ once: true }}
          >
            {points.map((p: any, i: number) => (
              <motion.div
                key={i}
                className="bg-yellow-100 p-6 rounded-lg border border-yellow-200 hover:bg-yellow-300/60 transition"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {iconMap[p.icon || 'Gift'] || <Gift className="h-8 w-8 text-yellow-700 mb-3" />}
                <h3 className="font-semibold text-gray-900 mb-2">{p.title}</h3>
                <p className="text-sm text-gray-700">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Activities;
